/**
 * User Scoped Storage Manager with sync get method (userId passed as an argument)
 * User scoped and versionable interface to use Local Storage. Can be used in any place.
 */
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';

export interface IUserScopedVersionableItem<D> {
  data: D;
  uid: string;
  version: string;
}

export const UserScopedVersionableStorageManagerSync = <T extends object>({
  storageKey,
  version,
  migrationCallback,
}: {
  storageKey: string;
  version: string;
  migrationCallback?: (
    item: IUserScopedVersionableItem<T>,
    version: string,
  ) => IUserScopedVersionableItem<T>;
}) => ({
  set: (item: T, uid: string): void => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    const savedObject: IUserScopedVersionableItem<T> = {
      data: item,
      uid,
      version,
    };
    window.localStorage.setItem(storageKey, JSON.stringify(savedObject));
  },

  get: (userId: string): T | null => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    try {
      const item = window.localStorage.getItem(storageKey);
      if (item) {
        const savedObject: IUserScopedVersionableItem<T> = JSON.parse(item);
        if (savedObject?.uid === userId && savedObject.version === version) {
          return savedObject?.data;
        }

        if (savedObject?.uid === userId && savedObject.version !== version && migrationCallback) {
          const migratedObject = migrationCallback(savedObject, version);
          window.localStorage.setItem(storageKey, JSON.stringify(migratedObject));
          return migratedObject.data;
        }
      }

      return null;
    } catch (e) {
      const errorMessage = `UserScopedVersionableStorageManager Error parsing state from local storage; storageKey ${storageKey}`;
      //Wrapping SentryMinimal in try-catch as we are not sure if sentry is available in all frontends
      try {
        SentryMinimal().captureMessage(errorMessage);
      } catch (e) {
        console.error(errorMessage);
      }
      return null;
    }
  },
});
